import React, { useState } from "react";
import { Table, Tag, Dropdown, MenuProps } from "antd";
import type { ColumnsType } from "antd/es/table";
import type { Referral } from "../../../../core/data/redux/recruitment/types";
import ApplicationPagination from "../../applications/components/ApplicationPagination";
import { humanReadableDate } from "../../../../helper/dateTimeHelper";

interface ReferralTableProps {
  data: Referral[];
  loading: boolean;
  onConvert?: (referralId: number) => void;
  onUpdateStatus?: (referralId: number, status: string) => void;
  onApproveBonus?: (referralId: number) => void;
  onProcessPayment?: (referralId: number) => void;
}

const STATUS_COLOR: Record<string, string> = {
  pending: "orange",
  under_review: "blue",
  shortlisted: "cyan",
  interviewed: "purple",
  selected: "green",
  joined: "success",
  rejected: "red",
  withdrawn: "default",
};

const ReferralTable: React.FC<ReferralTableProps> = ({
  data,
  loading,
  onConvert,
  onUpdateStatus,
  onApproveBonus,
  onProcessPayment,
}) => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const totalCount = data.length;
  const startIdx = (page - 1) * limit;
  const paginatedData = data.slice(startIdx, startIdx + limit);

  const getActionItems = (r: Referral): MenuProps["items"] => {
    const items: MenuProps["items"] = [];

    // Pending -> Approve (Convert to Application)
    if (
      onConvert &&
      !r.application_id &&
      r.status !== "rejected" &&
      r.status !== "withdrawn" &&
      r.status !== "joined"
    ) {
      items.push({
        key: "convert",
        icon: <i className="ti ti-transform" style={{ color: "#10b981" }} />,
        label: "Approve (Convert to Application)",
        onClick: () => onConvert(r.id),
      });
      items.push({ type: "divider" });
    }

    if (onUpdateStatus) {
      if (r.status === "joined" || r.status === "hired") {
        if (r.bonus_status === "pending" || !r.bonus_status) {
          if (onApproveBonus) {
            items.push({
              key: "approve_bonus",
              icon: <i className="ti ti-gift" style={{ color: "#8b5cf6" }} />,
              label: "Approve Bonus",
              onClick: () => onApproveBonus(r.id),
            });
          }
        } else if (r.bonus_status === "approved") {
          if (onProcessPayment) {
            items.push({
              key: "process_payment",
              icon: <i className="ti ti-cash" style={{ color: "#10b981" }} />,
              label: "Process Payment",
              onClick: () => onProcessPayment(r.id),
            });
          }
        }
      }

      // Reject Referral
      if (
        !r.application_id &&
        r.status !== "rejected" &&
        r.status !== "withdrawn" &&
        r.status !== "joined"
      ) {
        const lastItem = items[items.length - 1];
        if (
          items.length > 0 &&
          lastItem &&
          "type" in lastItem &&
          lastItem.type !== "divider"
        ) {
          items.push({ type: "divider" });
        }
        items.push({
          key: "reject",
          icon: <i className="ti ti-x" />,
          danger: true,
          label: "Reject Referral",
          onClick: () => onUpdateStatus(r.id, "rejected"),
        });
      }
    }

    return items;
  };

  const columns: ColumnsType<Referral> = [
    {
      title: "Sr.",
      key: "srNo",
      width: 55,
      align: "center",
      render: (_, __, index) => (
        <span style={{ fontSize: 12, color: "#6b7280" }}>
          {(page - 1) * limit + index + 1}
        </span>
      ),
    },
    {
      title: "Candidate",
      key: "candidate",
      width: 220,
      render: (_, r) => (
        <div>
          <div className="fw-medium fs-14">{r.candidate_name}</div>
          <span className="text-muted fs-12">{r.candidate_email}</span>
        </div>
      ),
    },
    {
      title: "Position",
      dataIndex: "job_title",
      key: "job_title",
      width: 180,
      render: (val) => <span className="fs-13">{val}</span>,
    },
    ...(onConvert || onUpdateStatus
      ? [
        {
          title: "Referred By",
          key: "referrer",
          width: 180,
          render: (_: any, r: Referral) => (
            <div>
              <div className="fs-13">{r.referrer_name}</div>
            </div>
          ),
        },
      ]
      : []),
    {
      title: "Relationship",
      dataIndex: "relationship",
      key: "relationship",
      width: 120,
      render: (val) => (
        <span className="fs-12 text-muted text-capitalize">
          {val?.replace(/_/g, " ")}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 130,
      render: (status) => (
        <Tag
          color={STATUS_COLOR[status] || "default"}
          style={{ textTransform: "capitalize" }}>
          {status?.replace(/_/g, " ")}
        </Tag>
      ),
    },
    {
      title: "Referred",
      dataIndex: "created_at",
      key: "created_at",
      width: 110,
      render: (d) => (
        <span className="text-muted fs-12">
          <i className="ti ti-calendar me-1" />
          {humanReadableDate(d)}
        </span>
      ),
    },
    {
      title: "Bonus",
      key: "bonus",
      width: 140,
      render: (_, r) =>
        r.bonus_amount ? (
          <Tag
            color={
              r.bonus_status === "full_paid"
                ? "green"
                : r.bonus_status === "approved"
                  ? "blue"
                  : "orange"
            }>
            ₹{r.bonus_amount.toLocaleString()} ·{" "}
            {r.bonus_status?.replace(/_/g, " ") || "Pending"}
          </Tag>
        ) : (
          <span className="text-muted fs-12">—</span>
        ),
    },
    ...(onConvert || onUpdateStatus
      ? [
        {
          title: "Actions",
          key: "actions",
          width: 80,
          align: "center" as const,
          render: (_: any, r: Referral) => {
            const items = getActionItems(r) || [];
            if (items.length === 0) return null;
            return (
              <Dropdown
                menu={{ items }}
                trigger={["click"]}
                placement="bottomRight">
                <button
                  className="btn btn-icon btn-sm btn-light"
                  style={{ borderRadius: "6px" }}
                  onClick={(e) => e.stopPropagation()}>
                  <i className="ti ti-dots-vertical" />
                </button>
              </Dropdown>
            );
          },
        },
      ]
      : []),
  ];

  return (
    <div>
      {loading ? (
        <div
          className="d-flex justify-content-center align-items-center bg-white"
          style={{
            minHeight: 400,
            borderRadius: 12,
            border: "1px solid #e2e8f0",
          }}>
          <div className="text-center">
            <div
              className="spinner-border text-primary"
              role="status"
              style={{ width: "3rem", height: "3rem" }}>
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-muted">Loading referrals...</p>
          </div>
        </div>
      ) : data.length > 0 ? (
        <div
          className="bg-white"
          style={{
            borderRadius: 12,
            overflow: "hidden",
            border: "1px solid #e2e8f0",
          }}>
          <Table
            className="custom-data-table"
            columns={columns}
            dataSource={paginatedData}
            rowKey="id"
            size="middle"
            pagination={false}
            scroll={{ x: 1100 }}
            rowClassName={(_, index) =>
              index % 2 === 0 ? "data-table-row-light" : "data-table-row-dark"
            }
          />
          <ApplicationPagination
            currentPage={page}
            totalPages={Math.ceil(totalCount / limit)}
            total={totalCount}
            limit={limit}
            hasNextPage={startIdx + limit < totalCount}
            hasPrevPage={page > 1}
            onPageChange={(p) => setPage(p)}
            onLimitChange={(l) => {
              setLimit(l);
              setPage(1);
            }}
          />
        </div>
      ) : (
        <div
          className="d-flex justify-content-center align-items-center bg-white"
          style={{
            minHeight: 400,
            borderRadius: 12,
            border: "1px solid #e2e8f0",
          }}>
          <div className="text-center">
            <i
              className="ti ti-user-off text-muted"
              style={{ fontSize: "4rem" }}
            />
            <h5 className="mt-3 mb-2 fw-semibold">No Referrals Found</h5>
            <p className="text-muted mb-0">
              No referrals match the current filters
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferralTable;
